import { t } from '@lingui/core/macro';
import React, { createContext, useContext, useCallback, useState } from 'react';

import { CancelToken, promiseWithResolvers, sleep } from 'backend-client-web';
import { SVGReactComponent, UnlockMethods } from 'client-web-sdk';

import ConfirmationModal, { ConfirmationModalProps } from 'src/components/modal/ConfirmationModal';

import { RequestUserConfirmationResult } from './useRequestUserConfirmation';

export interface UserConfirmationConfig {
  description: string;
  unlockMethods: UnlockMethods;
  requestUserConfirmation: (
    cancelToken: CancelToken | undefined,
  ) => Promise<RequestUserConfirmationResult>;
  retry: () => void;
}

export type ActionSummaryConfig = React.PropsWithChildren<{
  headline?: string;
  width?: string;
  loading?: boolean;
  illustration?: SVGReactComponent;
  action?: string;
  actionDisabled?: boolean;
  danger?: boolean;
}>;

type ModalConfig = {
  actionSummary?: ActionSummaryConfig;
  userConfirmation?: UserConfirmationConfig;
};

export interface ConfirmationModalContextValue {
  confirm: (modalConfig: ModalConfig) => Promise<boolean>;
}

const ConfirmationModalContext = createContext<ConfirmationModalContextValue>({
  confirm: async (props) => {
    if (props.actionSummary) {
      // eslint-disable-next-line no-alert, no-restricted-globals
      if (!confirm(props.actionSummary.headline)) {
        return false;
      }
    }
    if (props.userConfirmation) {
      const result = await props.userConfirmation.requestUserConfirmation(undefined);
      if (result !== RequestUserConfirmationResult.accepted) {
        return false;
      }
    }
    return true;
  },
});

export function ConfirmationModalContextProvider({ children }: React.PropsWithChildren<{}>) {
  const [modalProps, setModalProps] = useState<ConfirmationModalProps>();

  const confirm = useCallback(async (newModalConfig: ModalConfig) => {
    const { promise, resolve, reject } = promiseWithResolvers<boolean>();
    const confirmationCancelToken = new CancelToken();

    function showActionSummary(as: ActionSummaryConfig) {
      setModalProps({
        isOpen: true,
        state: 'actionSummary',
        width: as.width,
        headline: as.headline ?? '',
        illustration: as.illustration,
        action: as.action ?? t`OK`,
        actionDisabled: as.actionDisabled,
        actionType:
          newModalConfig.userConfirmation ? 'userConfirmation'
          : as.danger ? 'danger'
          : undefined,
        children: as.children,
        close: () => {
          resolve(false);
          setModalProps(undefined);
        },
        confirm: () => {
          if (newModalConfig.userConfirmation) {
            runUserConfirmation(newModalConfig.userConfirmation).catch((reason) => reject(reason));
          } else {
            setModalProps(undefined);
            resolve(true);
          }
        },
      });
    }

    async function runUserConfirmation(uc: UserConfirmationConfig) {
      setModalProps({
        isOpen: true,
        state: 'userConfirmation',
        description: uc.description,
        unlockMethods: uc.unlockMethods,
        width: newModalConfig.actionSummary?.width,
        result: undefined,
        close: () => {
          confirmationCancelToken.cancel();
          setModalProps(undefined);
        },
        cancel: () => {
          confirmationCancelToken.cancel();
        },
        retry: uc.retry,
      });
      const result = await uc.requestUserConfirmation(confirmationCancelToken);
      setModalProps((p) => (p?.state === 'userConfirmation' ? { ...p, result } : p));
      // Short timeout to allow push state animation to play before the confirmed action happens
      await sleep(900);
      // Resolve confirmation timeout, allowing action to take place
      resolve(result === RequestUserConfirmationResult.accepted);
      // Close modal if accepted or canceled. In the other states modal stays ope to allow user to
      // retry.
      if (
        result === RequestUserConfirmationResult.accepted ||
        result === RequestUserConfirmationResult.canceled
      ) {
        // Delay by short time. This allows UI that updates due to the action to change while the
        // modal is still open instead of flickering once it is closed.
        await sleep(100);
        setModalProps(undefined);
      }
    }

    if (newModalConfig.actionSummary) {
      showActionSummary(newModalConfig.actionSummary);
    } else if (newModalConfig.userConfirmation) {
      runUserConfirmation(newModalConfig.userConfirmation).catch((reason) => reject(reason));
    }

    return promise;
  }, []);

  return (
    <ConfirmationModalContext.Provider value={{ confirm }}>
      {children}
      <ConfirmationModal
        {...(modalProps ?
          { ...modalProps, isOpen: true }
        : {
            isOpen: false,
            state: 'actionSummary',
            action: '',
            headline: '',
            close: () => setModalProps(undefined),
            confirm: () => {},
          })}
      />
    </ConfirmationModalContext.Provider>
  );
}

export function useConfirmationModalContext(defaultModalConfig?: ModalConfig) {
  const { confirm } = useContext(ConfirmationModalContext);
  const confirmWithDefaults = useCallback(
    async (modalConfig?: ModalConfig) => {
      return confirm({
        actionSummary: mergeConfigs('actionSummary', defaultModalConfig, modalConfig),
        userConfirmation:
          defaultModalConfig?.userConfirmation || modalConfig?.userConfirmation ?
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            { ...defaultModalConfig?.userConfirmation!, ...modalConfig?.userConfirmation! }
          : undefined,
      });
    },
    [confirm, defaultModalConfig],
  );
  return { confirm: confirmWithDefaults };
}

function mergeConfigs<K extends string, V, O extends { [X in K]+?: V }>(
  k: K,
  defaultConfig: O | undefined,
  overrideConfig: O | undefined,
): V | undefined {
  const overrideKeyValue = overrideConfig?.[k];
  const defaultKeyValue = defaultConfig?.[k];

  // Set in default, see if it is unset in override
  if (defaultKeyValue) {
    // No override at all, just use default config
    if (!overrideConfig) {
      return defaultKeyValue;
    }
    // Key is not set in override, we'll use default config
    if (!(k in overrideConfig)) {
      return defaultKeyValue;
    }
    // Set in both, merge
    if (overrideKeyValue) {
      return { ...defaultKeyValue, ...overrideKeyValue };
    }
    // This leaves the case where `k` is explicity set to undefined in the override config. This
    // means the user has unset the default config, so we don't merge it.
    return undefined;
  }

  // Key set in override config but not in default. Use override
  if (overrideKeyValue) {
    return overrideKeyValue;
  }

  // Key not set in either, no config set
  return undefined;
}
